import React, { useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { OrderCardCallbacks, PLATFORM_COLORS, QueuePosition, UnifiedOrder, UnifiedOrderItem } from './types';
import ExcelLikeField from '../ExcelLikeField';
import HoverZoomImage from '../HoverZoomImage';
import { S } from './cardStyles';

interface Props {
  order: UnifiedOrder;
  onCounterReceive?: OrderCardCallbacks['onCounterReceive'];
  // Same handler the customer cards use: 進貨店家 is product metadata keyed by SKU, so
  // editing it here is the same write — and a brand-new SKU ordered for the counter is
  // usually the one that HAS no tag yet (Magnus 2026-09-22).
  onUpdateField?: OrderCardCallbacks['onUpdateField'];
  counterQueue?: Map<string, QueuePosition>;
  // 移除 a line / 取消 the parcel. Both cancel only what is still awaited; received
  // pieces are physically in the building and stay in stock (Magnus 2026-10-25).
  onCancelLine?: OrderCardCallbacks['onCounterCancelLine'];
  onCancelOrder?: OrderCardCallbacks['onCounterCancelOrder'];
  // Same 進貨 link column the customer cards show (admin only, Magnus 2026-10-25).
  showLinks?: boolean;
  getProductLink?: OrderCardCallbacks['getProductLink'];
}
const remainingOf = (item: UnifiedOrderItem) => item.counterLine?.remainingQty ??
  (item.received ? 0 : Math.max(0, item.quantity - (item.counterLine?.receivedQty || 0)));

const ACCENT = PLATFORM_COLORS.counter;
const ACCENT_HOVER = '#1f2b3a';

/**
 * The 門市 receiving card.
 *
 * It wears the SAME chrome as a customer card — same shell, same header row, same items
 * table (Magnus 2026-09-22) — because 出貨人員 work one queue and a card that looks foreign
 * is a card they read slowly. What stays different is only what must: the accent stripe and
 * the 門市補貨 chip, no customer row, and a 收貨 cell that takes an explicit quantity and an
 * explicit location instead of a checkbox, because this receipt moves real stock into the
 * counter's inventory.
 */
export default function CounterOrderCard({
  order, onCounterReceive, onUpdateField, counterQueue, onCancelLine, onCancelOrder, showLinks, getProductLink,
}: Props) {
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [locations, setLocations] = useState<Record<string, 'office' | 'store'>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [uncertain, setUncertain] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const receive = async (item: UnifiedOrderItem, qty: number, location: 'office' | 'store') => {
    if (busy[item.id] || !onCounterReceive) return;
    setBusy(p => ({ ...p, [item.id]: true }));
    try {
      await onCounterReceive(order, item, qty, location);
      setQuantities(p => { const next = { ...p }; delete next[item.id]; return next; });
      setUncertain(p => ({ ...p, [item.id]: false })); setErrors(p => ({ ...p, [item.id]: '' }));
    } catch (e: any) {
      setUncertain(p => ({ ...p, [item.id]: !(e?.response?.status >= 400 && e.response.status < 500 && ![408, 429].includes(e.response.status)) }));
      setErrors(p => ({ ...p, [item.id]: e?.response?.data?.error || e?.message || '收貨未確認，請重試原操作' }));
    } finally { setBusy(p => ({ ...p, [item.id]: false })); }
  };
  const cancelLine = async (item: UnifiedOrderItem, remaining: number) => {
    if (busy[item.id] || !onCancelLine) return;
    const received = item.counterLine?.receivedQty || 0;
    if (!window.confirm(`移除「${item.productName}」${item.variantOrSpec ? `（${item.variantOrSpec}）` : ''}？
` +
      `取消待收的 ${remaining} 件${received ? `；已收到的 ${received} 件保留在庫存` : ''}。`)) return;
    setBusy(p => ({ ...p, [item.id]: true }));
    try {
      await onCancelLine(order, item);
      setErrors(p => ({ ...p, [item.id]: '' }));
    } catch (e: any) {
      setErrors(p => ({ ...p, [item.id]: e?.response?.data?.error || e?.message || '移除失敗，請重試' }));
    } finally { setBusy(p => ({ ...p, [item.id]: false })); }
  };
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const cancelOrder = async () => {
    if (cancellingOrder || !onCancelOrder) return;
    const open = order.counterOrder?.pieceOpen ?? 0;
    const got = order.counterOrder?.pieceReceived ?? 0;
    if (!window.confirm(`取消${order.orderNumber}？
取消所有待收的 ${open} 件${got ? `；已收到的 ${got} 件保留在庫存` : ''}。`)) return;
    setCancellingOrder(true); setOrderError('');
    try { await onCancelOrder(order); }
    catch (e: any) { setOrderError(e?.response?.data?.error || e?.message || '取消失敗，請重試'); }
    finally { setCancellingOrder(false); }
  };
  const anyReceived = order.receivedCount > 0;
  const counter = order.counterOrder;
  // Finished lines (nothing left to receive) are hidden — they only took space in the
  // queue (Magnus 2026-10-15). A line with an error on it stays, so the message is seen.
  const openItems = order.items.filter(item => remainingOf(item) > 0 || errors[item.id]);
  const doneItems = order.items.filter(item => !openItems.includes(item));
  const missingTotal = doneItems.reduce((n, item) => n + (item.missingCount || 0), 0);
  return <Card sx={{ ...S.card, borderLeft: `4px solid ${ACCENT}` }}>
    {/* ═══ HEADER ═══ (no customer row — there is no customer) */}
    <Box sx={{ px: { xs: 2, md: 3.5 }, pt: 3, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Chip label="門市補貨" size="small"
          sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700, fontSize: '0.8rem', height: 28, borderRadius: '8px' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a', fontFamily: '"DM Mono", monospace' }}>
          {order.orderNumber}
        </Typography>
        <Box sx={{
          px: 1.5, py: 0.5, borderRadius: '8px',
          bgcolor: order.allReceived ? '#dcfce7' : anyReceived ? '#fef3c7' : '#f5f3f0',
          fontWeight: 700, fontSize: '0.85rem',
          color: order.allReceived ? '#15803d' : anyReceived ? '#92400e' : '#78716c',
          fontFamily: '"DM Mono", monospace',
        }}>{order.receivedCount}/{order.totalCount}</Box>
        {order.allReceived && <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 22 }} />}
        {onCancelOrder && !order.allReceived && <Button size="small" variant="outlined" color="error"
          disabled={cancellingOrder} onClick={cancelOrder} sx={{ ml: 'auto', borderRadius: '8px', fontWeight: 700 }}>
          {cancellingOrder ? '取消中…' : '取消門市訂單'}
        </Button>}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
        <Box sx={S.metaChip}>{order.orderDateDisplay}</Box>
        {!!counter && <Box sx={S.metaChip}>訂 {counter.piecePlanned} 件 · 已收 {counter.pieceReceived} · 待收 {counter.pieceOpen}</Box>}
      </Box>
      {orderError && <Alert severity="warning" sx={{ mt: 1.5 }}>{orderError}</Alert>}
    </Box>
    {order.allReceived && <Box sx={{ px: { xs: 2, md: 3.5 }, pb: 2 }}>
      <Alert severity="success">這張單已結束等貨；已到貨商品的移轉及上架，請看「門市訂單進度」。</Alert>
    </Box>}

    {/* ═══ ITEMS TABLE ═══ same columns as a customer card; 收貨 does more work ═══ */}
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="medium">
        <TableHead>
          <TableRow sx={S.tableHeader}>
            <TableCell width="85px" align="center">圖片</TableCell>
            <TableCell>產品名稱</TableCell>
            <TableCell width="13%">規格</TableCell>
            <TableCell width="50px" align="center">數量</TableCell>
            <TableCell width="9%">SKU</TableCell>
            <TableCell width="11%">標籤</TableCell>
            {showLinks && <TableCell width="55px" align="center">連結</TableCell>}
            <TableCell width="34%">收貨</TableCell>
            {onCancelLine && <TableCell width="50px" align="center">移除</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {openItems.map(item => {
            const remaining = remainingOf(item);
            const qty = quantities[item.id] ?? String(remaining);
            const amount = Number(qty);
            const location = locations[item.id] || 'office';
            const locked = busy[item.id] || uncertain[item.id];
            const queue = counterQueue?.get(item.id);
            return <TableRow key={item.id} sx={S.tableRow(item.received)}>
              <TableCell align="center">
                {item.imageUrl
                  ? <HoverZoomImage src={item.imageUrl} width={80} height={80}
                      thumbSx={{ borderRadius: '10px', border: '1px solid #e8e5e1' }} />
                  : <Box sx={{ width: 80, height: 80, bgcolor: '#f5f3f0', borderRadius: '10px' }} />}
              </TableCell>

              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.9rem', color: '#1a1a1a' }}>{item.productName}</Typography>
                  {item.received && <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 16, flexShrink: 0 }} />}
                </Box>
                <Typography variant="body2" sx={{ color: '#8c8580', fontSize: '0.78rem', mt: 0.25 }}>
                  訂 {item.quantity} · 已收 {item.counterLine?.receivedQty || 0} · 待收 {remaining}
                  {!!item.counterLine?.cancelledQty && ` · 已取消餘量 ${item.counterLine.cancelledQty}`}
                </Typography>
                {/* Allocation is by earliest ordered_at, so a short arrival can legitimately
                    go to an older order — said on the row that would otherwise take it. */}
                {queue && queue.aheadPieces > 0 && <Typography variant="body2" sx={{ color: '#92400e', fontSize: '0.78rem', mt: 0.25 }}>
                  這個色碼有 {queue.aheadPieces} 件更早的訂單在等{queue.aheadLabel ? `（${queue.aheadLabel} 先）` : ''}
                </Typography>}
              </TableCell>

              <TableCell>
                <Typography sx={{ fontSize: '0.85rem', color: '#57534e' }}>{item.variantOrSpec || '—'}</Typography>
              </TableCell>

              <TableCell align="center">
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 32, height: 32, borderRadius: '8px',
                  bgcolor: item.quantity > 1 ? '#fef3c7' : '#f5f3f0',
                  border: item.quantity > 1 ? '1px solid #fde68a' : 'none',
                  fontWeight: 700, fontSize: '0.9rem',
                  color: item.quantity > 1 ? '#92400e' : '#57534e',
                  fontFamily: '"DM Mono", monospace',
                }}>{item.quantity}</Box>
              </TableCell>

              <TableCell>
                <Typography sx={{ fontSize: '0.82rem', color: '#78716c', fontFamily: '"DM Mono", monospace' }}>{item.sku || '—'}</Typography>
              </TableCell>

              {/* 進貨店家, per line and editable — it replaces a parcel-level chip that could
                  not be corrected, and a newly ordered SKU usually has no tag yet. */}
              <TableCell>
                <ExcelLikeField value={item.tag || ''} placeholder="標籤"
                  onSave={v => onUpdateField && onUpdateField(order, item, 'tag', v)} />
              </TableCell>

              {showLinks && <TableCell align="center">
                {(() => {
                  const info = getProductLink?.(item.sku);
                  const links = info?.allLinks?.length ? info.allLinks : info?.link ? [info.link] : [];
                  if (!links.length) return <Typography sx={{ color: '#c4bfba', fontSize: '0.8rem' }}>—</Typography>;
                  return <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                    {links.map((url, idx) => <IconButton key={idx} size="small"
                      onClick={() => window.open(url, '_blank', 'noopener')}
                      sx={{ color: idx === 0 ? '#3b82f6' : '#8b7355', width: 28, height: 28 }}>
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>)}
                  </Box>;
                })()}
              </TableCell>}

              <TableCell>
                {remaining > 0 ? <>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <TextField label="本次收到" size="small" type="number" value={qty} disabled={!!locked}
                      inputProps={{ min: 1, max: remaining }} sx={{ width: 100 }}
                      onChange={e => setQuantities(p => ({ ...p, [item.id]: e.target.value }))} />
                    <TextField select label="貨實際在哪裡" size="small" value={location} disabled={!!locked} sx={{ minWidth: 150 }}
                      onChange={e => setLocations(p => ({ ...p, [item.id]: e.target.value as 'office' | 'store' }))}>
                      <MenuItem value="office">辦公室／倉庫</MenuItem><MenuItem value="store">NOKE 門市</MenuItem>
                    </TextField>
                    <Button variant="contained" sx={{ ...S.btn, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
                      disabled={!onCounterReceive || busy[item.id] || !Number.isInteger(amount) || amount < 1 || amount > remaining}
                      onClick={() => receive(item, amount, location)}>
                      {uncertain[item.id] ? '重試原收貨' : location === 'office' ? '確認辦公室收到' : '確認門市收到'}
                    </Button>
                  </Stack>
                  {amount > 0 && amount < remaining && <Typography color="warning.main" variant="body2" sx={{ mt: 0.75 }}>
                    其餘 {remaining - amount} 件繼續等貨
                  </Typography>}
                </> : <Chip size="small" label={item.missingCount ? `歷史短少 ${item.missingCount} 件` : '等貨已結束'} />}
                {errors[item.id] && <Alert severity="warning" sx={{ mt: 1 }}>{errors[item.id]}</Alert>}
              </TableCell>

              {onCancelLine && <TableCell align="center">
                {remaining > 0 && <Tooltip title="移除這筆（取消待收件數）">
                  <span><IconButton size="small" color="error" disabled={!!locked}
                    onClick={() => cancelLine(item, remaining)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton></span>
                </Tooltip>}
              </TableCell>}
            </TableRow>;
          })}
        </TableBody>
      </Table>
    </TableContainer>

    <Box sx={{ px: { xs: 2, md: 3.5 }, py: 2 }}>
      {doneItems.length > 0 && <Typography variant="body2" sx={{ color: '#78716c', mb: 0.5 }}>
        已收完 {doneItems.length} 項（已隱藏）{missingTotal > 0 && ` · 歷史短少 ${missingTotal} 件`}
      </Typography>}
      <Typography variant="body2" color="text.secondary">辦公室收到 → 待送 NOKE；門市收到 → 待上架。未到貨的餘量繼續保留。</Typography>
    </Box>
  </Card>;
}
